import { useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  LayoutDashboard,
  Lightbulb,
  PartyPopper,
  CalendarDays,
  Briefcase,
  Users,
  Trophy,
  Workflow,
  ShieldCheck,
  Archive,
  Tag,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { getEventTeamPerms } from '../utils/eventTeamPermissions';
import { EventDashboardPanel } from '../components/eventteam/panels/EventDashboardPanel';
import { EventIdeasPanel } from '../components/eventteam/panels/EventIdeasPanel';
import { EventsListPanel } from '../components/eventteam/panels/EventsListPanel';
import { EventCalendarPanel } from '../components/eventteam/panels/EventCalendarPanel';
import { EventProjectsPanel } from '../components/eventteam/panels/EventProjectsPanel';
import { EventRosterPanel } from '../components/eventteam/panels/EventRosterPanel';
import { EventPointsPrefixesPanel } from '../components/eventteam/panels/EventPointsPrefixesPanel';
import { EventWorkflowsPanel } from '../components/eventteam/panels/EventWorkflowsPanel';
import { EventClearancePanel } from '../components/eventteam/panels/EventClearancePanel';
import { EventArchivePanel } from '../components/eventteam/panels/EventArchivePanel';
import { EventTagsPanel } from '../components/eventteam/panels/EventTagsPanel';
import { PageHeader } from '../components/ui/PageHeader';
import { EmptyState } from '../components/ui/EmptyState';
import { StatTile } from '../components/ui/StatTile';
import { PanelTour } from '../components/ui/PanelTour';
import { eventPanelTour } from '../config/panelTours';
import { useTourNavigation } from '../hooks/useTourNavigation';

type TabId =
  | 'dashboard'
  | 'ideas'
  | 'events'
  | 'calendar'
  | 'projects'
  | 'team'
  | 'points'
  | 'workflows'
  | 'clearance'
  | 'archive'
  | 'tags';

const TABS: { id: TabId; label: string; icon: React.ComponentType<{ className?: string }>; adminOnly?: boolean }[] = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { id: 'ideas', label: 'Ideas', icon: Lightbulb },
  { id: 'events', label: 'Events', icon: PartyPopper },
  { id: 'calendar', label: 'Calendar', icon: CalendarDays },
  { id: 'projects', label: 'Projects', icon: Briefcase },
  { id: 'team', label: 'Team', icon: Users },
  { id: 'points', label: 'Player rewards', icon: Trophy },
  { id: 'workflows', label: 'Workflows', icon: Workflow },
  { id: 'clearance', label: 'Command activity', icon: ShieldCheck },
  { id: 'archive', label: 'Archive', icon: Archive },
  { id: 'tags', label: 'Tags', icon: Tag, adminOnly: true },
];

/**
 * Event Team hub — Dashboard · Ideas · Events · Projects · Team · Points & Prefixes ·
 * Workflows · Clearance · Archive · Tags. Tab state is URL-synced (`?tab=`).
 */
export const EventTeamView = () => {
  const { identity, isLoading: authLoading } = useAuth();
  const { tourNavigation, activeStep: tourStep } = useTourNavigation();
  const navigate = useNavigate();
  const isDev = import.meta.env.DEV;
  useDocumentTitle('Event Team');

  const perms = useMemo(() => getEventTeamPerms(identity, isDev), [identity, isDev]);

  const [searchParams, setSearchParams] = useSearchParams();
  const rawTab = searchParams.get('tab') as TabId | null;
  const visibleTabs = TABS.filter((t) => !t.adminOnly || perms.canAdmin);
  const primaryIds: TabId[] = ['dashboard', 'events', 'calendar', 'points', 'team', 'clearance'];
  const primaryTabs = primaryIds.map(id => visibleTabs.find(t => t.id === id)!);
  const moreTabs = visibleTabs.filter(t => !primaryIds.includes(t.id));
  const activeTab: TabId = visibleTabs.some((t) => t.id === rawTab) ? (rawTab as TabId) : 'dashboard';
  const tagFilter = searchParams.get('tag_id') || '';

  const setTab = (tab: TabId, extra?: Record<string, string>) => {
    const next = new URLSearchParams();
    next.set('tab', tab);
    if (extra) {
      for (const [k, v] of Object.entries(extra)) {
        if (v) next.set(k, v);
      }
    }
    setSearchParams(next);
  };

  if (!perms.canView) {
    return (
      <EmptyState
        tone="warning"
        icon={ShieldCheck}
        title="You don't have permission to view the Event Team panel."
        description={
          <>
            Ask an admin for the <code className="font-mono text-xs">argus_event_panel</code> group.
          </>
        }
      />
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Event Team"
        title="Event Team"
        icon={PartyPopper}
        description="Plan the next event, find your people, and check what happened."
        actions={<div className="flex gap-2"><Link to="/forms" data-tour="event-feedback" className="glass-button-secondary">Forms & feedback</Link><PanelTour tourId="event-team" title="Event Team" steps={eventPanelTour(perms.canLead, perms.canPoints, perms.canAdmin)} enabled={perms.canView && !authLoading} {...tourNavigation} /></div>}
      >
        {/* Panel navigation */}
        <div className="flex flex-wrap items-center gap-3">
          <div data-tour="event-navigation" className="tabs flex-wrap max-w-full" role="tablist" aria-label="Event Team panels">
            {primaryTabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  data-tour={`event-${tab.id}`}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => setTab(tab.id)}
                  className="tab"
                  data-active={isActive ? 'true' : 'false'}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                </button>
              );
            })}
          </div>
          <select data-tour="event-more" aria-label="More event tools" className="glass-select text-sm w-full sm:w-auto" value={moreTabs.some(t => t.id === activeTab) ? activeTab : ''} onChange={e => { if (e.target.value) setTab(e.target.value as TabId); }}>
            <option value="" disabled>More tools…</option>
            <optgroup label="Planning">{moreTabs.filter(t => ['ideas', 'projects', 'workflows'].includes(t.id)).map(t => <option key={t.id} value={t.id}>{t.label}</option>)}</optgroup>
            <optgroup label="Management">{moreTabs.filter(t => !['ideas', 'projects', 'workflows'].includes(t.id)).map(t => <option key={t.id} value={t.id}>{t.label}</option>)}</optgroup>
          </select>
        </div>
      </PageHeader>

      <div>
        {activeTab === 'dashboard' && (
          <div className="space-y-5">
            <div className="grid md:grid-cols-3 gap-3">
              <StatTile label="What's next?" value="Upcoming events" hint="Find dates, owners and progress" icon={CalendarDays} onClick={() => setTab('events')} />
              <StatTile label="Who did what?" value="Command activity" hint="Search players, a server and a time range" icon={ShieldCheck} tone="accent" onClick={() => setTab('clearance')} />
              <StatTile label="Reward participation" value="Player rewards" hint="Look up any player's points and rewards" icon={Trophy} onClick={() => setTab('points')} />
            </div>
            <EventDashboardPanel onTagClick={(tagId) => setTab('events', { tag_id: tagId })} />
          </div>
        )}
        {activeTab === 'ideas' && <EventIdeasPanel perms={perms} />}
        {activeTab === 'events' && (
          <EventsListPanel
            perms={perms}
            initialTagId={tagFilter}
            onOpenEvent={(id) => navigate(`/event-team/events/${id}`)}
          />
        )}
        {activeTab === 'calendar' && (
          <EventCalendarPanel onOpenEvent={(id) => navigate(`/event-team/events/${id}`)} />
        )}
        {activeTab === 'projects' && (
          <EventProjectsPanel perms={perms} onOpenProject={(id) => navigate(`/event-team/projects/${id}`)} />
        )}
        {activeTab === 'team' && (
          <EventRosterPanel perms={perms} onOpenMember={(id) => navigate(`/event-team/members/${id}`)} />
        )}
        {activeTab === 'points' && <EventPointsPrefixesPanel perms={perms} />}
        {activeTab === 'workflows' && <EventWorkflowsPanel perms={perms} />}
        {activeTab === 'clearance' && (
          <EventClearancePanel perms={perms} tourQuickSearch={['players', 'server', 'time', 'search'].includes(tourStep ?? '')} onOpenCheck={(id) => navigate(`/event-team/checks/${id}`)} />
        )}
        {activeTab === 'archive' && <EventArchivePanel perms={perms} />}
        {activeTab === 'tags' && perms.canAdmin && <EventTagsPanel perms={perms} />}
      </div>
    </div>
  );
};
